import axios from 'axios';
import { APIOverrides } from "../../ApiOverrides"
import type { AthleteProps, AthleteStatProps, BestAvailableOrderProps, BestAvailableResponseProps, ContestStatProps, EventOrderStatProps, EventProps, ExternalPriceProps, LeagueProps, MarketProps, MarketSideOptionProps, MarketStatsProps, MatchProps, MyPlayerProps, OrderProps, PodcastProps, PositionProps, PublicPlayerProps, TeamProps, TeamRankingProps, TeamRecordProps, TournamentProps, TradeProps } from "../../types"
import moment, { type Moment } from 'moment-mini';
import { MarketMaketHelpers } from '../../MarketMaker/api';

let EVENT_SVC_API = ''
let AUTH_SVC_API = ''
let MK_SVC_API = ''
let SOCIAL_SVC_API = ''

export { MarketComponentApi, MarketComponentHelpers }

const MarketComponentApi = {
    setEnvironment: () => {
        const endpoints = APIOverrides.getEndpoints();
        EVENT_SVC_API = endpoints['EVENT_SVC_API'] as string;
        MK_SVC_API = endpoints['MK_SVC_API'] as string;
        AUTH_SVC_API = endpoints['AUTH_SVC_API'] as string;
        SOCIAL_SVC_API = endpoints['SOCIAL_SVC_API'] as string;
    },
    getMarkets:async():Promise<MarketProps[]> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/markets/all`);
            return resp.data.markets
        } catch (e) {
            console.log(e)
            return []
        }
    },
    createOrder: async(order:OrderProps):Promise<OrderProps | undefined> => {
        try {
            const resp = await axios.post(`${MK_SVC_API}/v1/orders/order/create`, { order });
            return resp.data.order
        } catch (e) {
            console.log(e);
            return undefined
        }
    },
    getPlayersByIds: async(player_ids:string[]):Promise<PublicPlayerProps[]> => {
        try {
            if(player_ids.length == 0){ return [] }
            const resp = await axios.post(`${AUTH_SVC_API}/v1/players/bulk/get`, { player_ids });
            return resp.data.players
        } catch (e) {
            return []
        }
    },
    getOrdersByIds: async(order_ids:string[]):Promise<OrderProps[]> => {
        if(order_ids.length == 0){ return [] }
        try {
            const resp = await axios.post(`${MK_SVC_API}/v1/orders/bulk/get`, { order_ids });
            return resp.data.orders
        } catch (e) {
            return []
        }
    },
    expireOpenOrders: async(event_id:string, event_type:string, market_id:string, order_ids?:string[]):Promise<boolean> => {
        try {
            await axios.post(`${MK_SVC_API}/v1/orders/expire`, { event_id, event_type, market_id, order_ids });
            return true
        } catch (e) {
            return false
        }
    },
    getMyDetails : async():Promise<MyPlayerProps | undefined> => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/players/player/me`)
            return resp.data.player   
        } catch (e) {
            return undefined
        }
    },
    getOrderById: async(order_id:string):Promise<OrderProps | undefined> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/orders/order/${order_id}`);
            return resp.data.order
        } catch (e) {
            return undefined
        }
    },
    getActiveEvents: async(league_id?:string):Promise<EventProps[]> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/events/active?expanded=true&league_id=${league_id}`);
            return resp.data.events
        } catch (e) {
            console.log(e);
            return []
        }
    },
    getScheduledTournamentsByTeams: async(team_ids:string):Promise<TournamentProps[]> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/tournaments/teams/${team_ids}`);
            return resp.data.tournaments
        } catch (e) {
            return []
        }
    },
    getScheduledTournamentsByAthletes: async(athlete_ids:string):Promise<TournamentProps[]> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/tournaments/athletes/${athlete_ids}`);
            return resp.data.tournaments
        } catch (e) {
            return []
        }
    },
    getMarketStatsByEvent: async(event_id:string, event_type:string):Promise<MarketStatsProps[]> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/markets/event/stats/${event_type}/${event_id}`);
            return resp.data.market_stats
        } catch (e) {
            console.log(e);
            return []
        }
    },
    getMarketStatsByEventIds: async(event_type:string, event_ids:string[]):Promise<MarketStatsProps[]> => {
        if(event_ids.length == 0){ return [] }
        try {
            const resp = await axios.post(`${MK_SVC_API}/v1/markets/event/stats/bulk/get`, { event_type, event_ids });
            return resp.data.market_stats
        } catch (e) {
            console.log(e);
            return []
        }
    },
    getActiveTournaments: async(league_id?:string):Promise<TournamentProps[]> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/tournaments/active?expanded=true&league_id=${league_id}`);
            if(resp.data.tournament_ids.length == 0){ return [] }
            const tournaments = await MarketComponentApi.getTournamentsByTournamentIds(resp.data.tournament_ids);
            return tournaments
        } catch (e) {
            console.log(e);
            return []
        }
    },
    getTradesByEventAndMarket: async(event_type:string, event_id:string, market_id:string, offset:number, side_id?:string):Promise<TradeProps[]> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/trades/event/all/${event_id}/${event_type}?market_id=${market_id}&side_id=${side_id}&offset=${offset}`);
            return resp.data.trades
        } catch (e) {
            console.log(e);
            return []
        }
    },
    getLatestTradesByEvents: async(event_type: string, event_ids:string[]):Promise<TradeProps[]> => {
        try {
            if(event_ids.length == 0){ return [] }
            const resp = await axios.post(`${MK_SVC_API}/v1/trades/event/latest/bulk/get`, { event_type, event_ids })
            return resp.data.trades
        } catch (e) {
            console.log(e)
            return []
        }
    },
    getExternalPrices: async(contest_type:string, contest_id:string, market_id:string, side?:string, participant_id?:string, participant_type?:string, external_name?:string):Promise<ExternalPriceProps[]> => {
        try {
            let url = `${EVENT_SVC_API}/v1/prices/latest/${contest_id}/${contest_type}?market_id=${market_id}`
            if(side){
                url += `&side=${side}`
            }
            if(participant_type && participant_id){
                url += `&participant_id=${participant_id}`
            }
            if(external_name){
                url += `&external_name=${external_name}`
            }
            const resp = await axios.get(url);
            return resp.data.prices

        } catch (e) {
            return []
        }
    },
    resolveMarket:async(market_resolve:{ event_id:string, event_type:string, market_id:string, athlete_stat?:AthleteStatProps, winnings_side?:string, side_id?:string, side_type?:string, no_bet?:boolean, no_bet_reason?:string, event_stat_value?:number }) => {
        try {
            await axios.post(`${MK_SVC_API}/v1/markets/market/resolve`, { market_resolve });
            return true
        } catch (e) {
            console.log(e);
            return false
        }
    },
    rollbackMarket:async(event_id:string, event_type:string, market_id:string, side_type?:string, side_id?:string, order_ids?:string[]):Promise<boolean> => {
        try {
            await axios.post(`${MK_SVC_API}/v1/orders/rollback`,  { event_id, event_type, market_id, side_type, side_id, order_ids });
            return true
        } catch (e) {
            console.log(e);
            return false
        }
    },
    getMyAction:async():Promise<OrderProps[]> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/orders/action/me`);
            return resp.data.orders
        } catch (e) {
            console.log(e);
            return []
        }
    },
    getMyHistory: async(player_id:string, range:number):Promise<OrderProps[]> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/orders/history/player/${player_id}?range=${range}`);
            return resp.data.orders
        } catch (e) {
            console.log(e)
            return []
        }

    },
    getEventsByEventIds: async(event_ids:string[]):Promise<EventProps[]> => {
        try {
            if(event_ids.length == 0){ return [] }
            const resp = await axios.post(`${EVENT_SVC_API}/v1/events/bulk/get`, { attribute:'event_id', values: event_ids })
            return resp.data.events
        } catch (e) {
            return []
        }
    },
    getEventsByDate: async(league_id:string, date:string, timezone?:string):Promise<{day:string, days:string[], events:EventProps[]}> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/events/league/date/${league_id}/${date}?timezone=${timezone}`);
            return resp.data
        } catch (e) {
            return { day: date, days:[], events: [] }
        }
    },
    getMatchesByMatchIds: async(match_ids:string[]):Promise<MatchProps[]> => {
        try {
            if(match_ids.length == 0){ return [] }
            const resp = await axios.post(`${EVENT_SVC_API}/v1/matches/bulk/get`, { attribute: 'match_id', values: match_ids })
            return resp.data.matches
        } catch (e) {
            return []
        }

    },
    getAthletesByIds : async(athlete_ids:string[]):Promise<AthleteProps[]> => {
        try {
            if(athlete_ids.length == 0){ return [] }
            const resp = await axios.post(`${EVENT_SVC_API}/v1/athletes/bulk/get`, { attribute: 'athlete_id', values: athlete_ids })
            return resp.data.athletes
        } catch (e) {
            return []
        }
    },
    getTournamentsByTournamentIds: async(tournament_ids:string[]):Promise<TournamentProps[]> => {
        try {
            if(tournament_ids.length == 0){ return [] }
            const resp = await axios.post(`${EVENT_SVC_API}/v1/tournaments/bulk/get`, { attribute: 'event_id', values: tournament_ids })
            return resp.data.tournaments
        } catch (e) {
            return []
        }
    },
    getTeamsByIds: async(team_ids:string[]) => {
        try {
            if(team_ids.length == 0){ return [] }
            const resp = await axios.post(`${EVENT_SVC_API}/v1/teams/bulk/get`, { attribute:'team_id', values: team_ids })
            return resp.data.teams
        } catch (e) {
            return []
        }

    },
    getTeamById: async(team_id:string):Promise<undefined | { team:TeamProps, team_rankings:TeamRankingProps[], team_records:TeamRecordProps[], league:LeagueProps }> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/teams/team/${team_id}`);
            return resp.data
        } catch (e) {
            return undefined
        }
    },
    getLeagues:async():Promise<LeagueProps[]> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/leagues`);
            return resp.data.leagues
        } catch (e) {
            return []
        }
    },
    getBestAvailableOrders:async():Promise<BestAvailableResponseProps> => {
        try {
            const resp = await axios.get(`${MK_SVC_API}/v1/orders/available`);
            return resp.data
        } catch (e) {
            return { events: [], tournaments: [], matches: [] }
        }
    },
    getAthletesByTeamId: async(team_id:string):Promise<AthleteProps[]> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/athletes/team/${team_id}`)
            return resp.data.athletes
        } catch (e) {
            console.log(e)
            return []
        }
    },
    getPodcastsByKeywords: async(keywords:string):Promise<PodcastProps[]> => {
        try {
            const resp = await axios.get(`${SOCIAL_SVC_API}/v1/podcasts/search/keywords?keywords=${keywords}`);
            return resp.data.podcasts
        } catch (e) {
            return []
        }
    },
    getMatchesByTournament: async(tournament_id:string):Promise<MatchProps[]> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/matches/tournament/${tournament_id}`);
            return resp.data.matches
        } catch (e) {
            console.log(e);
            return[]
        }
    },
    cancelOrders: async(order_ids:string[]):Promise<OrderProps[]> => {
        try {
            if(order_ids.length == 0){ return [] }
            const resp = await axios.post(`${MK_SVC_API}/v1/orders/bulk/cancel`, { order_ids });
            return resp.data.orders
        } catch (e) {
            return []
        }
    },
    getCompanyById: async(company_id:string) => {
        try {
            const resp = await axios.get(`${AUTH_SVC_API}/v1/companies/company/id/${company_id}`)
            return resp.data
        } catch (e) {
            return undefined
        }
    },
    getContestStatsByEvent: async(contest_id:string):Promise<ContestStatProps[]> => {
        try {
            const resp = await axios.get(`${EVENT_SVC_API}/v1/events/stats/event/${contest_id}`)
            return resp.data.contest_stats
        } catch (e) {
            console.log(e);
            return []
        }
    }
}


const MarketComponentHelpers = {
    getLiqudity: (event_order_stats:EventOrderStatProps[]):{ liquidity: number, open_order_count:number } => {
        let liquidity = event_order_stats.reduce((a,b) => a + b.open_order_amount, 0);
        let open_order_count = event_order_stats.reduce((a,b) => a + parseInt(b.open_order_count as string), 0);
        return { liquidity, open_order_count }
    },
    calcPotentialWinnings : (amt:number, odds:number):number => {
        if (odds > 0) {
            var earningsMultiplyer = ((odds/100) + 1);
            return earningsMultiplyer * amt;
        } else {
            var earningsMultiplyer = ((100/(Math.abs(odds))) + 1);
            return earningsMultiplyer * amt
        }
    },
    getMissingTournaments:(matches:MatchProps[], tournaments:TournamentProps[]) => {
        let needed_tourneys:string[] = []
        matches.map(m => {
            let tourney = tournaments.find(t => t.tournament_id == m.tournament_id);
            if(!tourney){ needed_tourneys.push(m.tournament_id) }
        })
        return needed_tourneys
    },
    extractAthleteIds: (athlete_ids?:string):string[] => {
        if(!athlete_ids || athlete_ids == 'undefined'){ return [] }
        let split_ids = athlete_ids.split(',');
        let ath_ids:string[] = []
        split_ids.map(id => {
            if(isNaN(parseInt(id))){ return }
            return ath_ids.push(id)
        });
        return ath_ids
    },
    formatBestAvailable:(ba_response:{ events:EventProps[], tournaments:TournamentProps[], matches:MatchProps[] }) => {
        let best_available:{ [key:string]: BestAvailableOrderProps[] } = {}
        let order_stats: { [key:string]: EventOrderStatProps[] } = {}
        let latest_trades: { [key:string]: TradeProps[] } = {}
        let athlete_ids:string[] = []
        let team_ids: string[] = []

        ba_response.events.map((e:EventProps) => {
            if(!e.supported_markets){ return }
            let ao:BestAvailableOrderProps[] = [], os:EventOrderStatProps[] = [], lt:TradeProps[] = []
            let supported_markets = e.supported_markets.filter(m => m)

            supported_markets.map(m => {
                m.available_orders?.map(o => ao.push(o))
                m.order_stats?.map(s => os.push(s))
                m.latest_trades?.map(t => {
                    if(t.side_type == 'athlete'){ athlete_ids.push(t.side_id) }
                    lt.push(t)
                })
            });
            best_available[`team:${e.event_id}`] = ao
            order_stats[`team:${e.event_id}`] = os
            latest_trades[`team:${e.event_id}`] = lt
        })
        ba_response.tournaments.map((e:TournamentProps) => {
            if(!e.supported_markets){ return }
            let ao:BestAvailableOrderProps[] = [], os:EventOrderStatProps[] = [], lt:TradeProps[] = []
            let supported_markets = e.supported_markets.filter(m => m)
            supported_markets.map(m => {
                m.available_orders?.map(o => ao.push(o))
                m.order_stats?.map(s => os.push(s))
                m.latest_trades?.map(t => {
                    if(t.side_type == 'athlete'){ athlete_ids.push(t.side_id) }
                    lt.push(t)
                })
            });
            best_available[`tournament:${e.tournament_id}`] = ao
            order_stats[`tournament:${e.tournament_id}`] = os
            latest_trades[`tournament:${e.tournament_id}`] = lt

        })
        ba_response.matches.map((e:MatchProps) => {
            if(!e.supported_markets){ return }
            if(e.participant_type == 'athlete'){ athlete_ids = athlete_ids.concat(e.participants) }
            let ao:BestAvailableOrderProps[] = [], os:EventOrderStatProps[] = [], lt:TradeProps[] = []
            let supported_markets = e.supported_markets.filter(m => m)
            supported_markets.map(m => {
                
                m.available_orders?.map(o => ao.push(o))
                m.order_stats?.map(s => os.push(s))
                m.latest_trades?.map(t => lt.push(t))
            });
            best_available[`match:${e.match_id}`] = ao
            order_stats[`match:${e.match_id}`] = os
            latest_trades[`match:${e.match_id}`] = lt

            let existing_tournament = order_stats[`tournament:${e.tournament_id}`]
            if(!existing_tournament){
                order_stats[`tournament:${e.tournament_id}`] = os
            } else {
                order_stats[`tournament:${e.tournament_id}`] = existing_tournament.concat(os)
            }

            let existing_tournament_trades = latest_trades[`tournament:${e.tournament_id}`]
            if(!existing_tournament_trades){
                latest_trades[`tournament:${e.tournament_id}`] = lt
            } else {
                latest_trades[`tournament:${e.tournament_id}`] = existing_tournament_trades.concat(lt)
            }

            let existing_tournament_orders = best_available[`tournament:${e.tournament_id}`]
            if(!existing_tournament_orders){
                best_available[`tournament:${e.tournament_id}`] = ao
            } else {
                best_available[`tournament:${e.tournament_id}`] = existing_tournament_orders.concat(ao)
            }
        })
        athlete_ids = [ ...new Set(athlete_ids.map(id => id)) ]
        return { best_available, order_stats, latest_trades, athlete_ids, team_ids }
    },
    getOrdersFromTrade:(trade:TradeProps, orders:OrderProps[]) => {
        if(trade.position_ids.length == 0){ return { found_orders:[], found_positions: [] } }
        let found_orders:OrderProps[] = []
        let found_positions:PositionProps[] = []
        trade.position_ids.map(id => {
            //Find the order that has this position!
            const order = orders.find(o => o.positions[0] && o.positions.find(p => p.position_id == id))
            if(order){ 
                found_orders.push(order) 
                let position = order.positions.find(p => p.position_id == id);
                if(position){ found_positions.push(position) }
            }
        })
        return { found_orders, found_positions }
    },
    sortMarketStats: (market_stats:MarketStatsProps[], markets:MarketProps[]):MarketProps[] => {
        return markets.sort((a,b) => {
            let a_volume = market_stats.filter(ms => ms.market_id == a.market_id).reduce((a,b) => a + b.stake, 0);
            let b_volume = market_stats.filter(ms => ms.market_id == b.market_id).reduce((a,b) => a + b.stake, 0);
            return b_volume - a_volume || parseInt(a.market_id) - parseInt(b.market_id)
        })
    },
    getOrderIdsFromStats: (market_stats:MarketStatsProps[]):string[] => {
        let order_ids:string[] = []
        market_stats.map(s => {
            order_ids = order_ids.concat(s.order_ids)
        });
        return order_ids
    },
    getContestDetails: (contest_type:string, event?:EventProps, tournament?:TournamentProps, match?:MatchProps):{ contest_title:string, scheduled_datetime:Moment } => {
        switch(contest_type){
            case 'team':
                if(!event){ return { contest_title: '', scheduled_datetime: moment() } }
                return { contest_title: event.event_title, scheduled_datetime: moment(event.scheduled_datetime) }
            case 'tournament':
                if(!tournament){ return { contest_title: '', scheduled_datetime: moment() } }
                return { contest_title: tournament.tournament_name, scheduled_datetime: moment(tournament.scheduled_datetime) }
            case 'match':
                if(!match){ return { contest_title: '', scheduled_datetime: moment() } }
                return { contest_title: match.match_title, scheduled_datetime: moment(match.scheduled_datetime) }
            default: return { contest_title: '', scheduled_datetime: moment() }
        }
    },
    getReconFromMarketStats: (ms:MarketStatsProps[]) => {
        let closed = ms.find(s => s.resolution_status == 'inprogress') ? false : true
        let stats = ms.filter(s => s.market_type == 'FOR_MONEY')
        let total_unresolved = ms.filter(s => s.resolution_status == 'inprogress').reduce((a,b) => a + b.stake, 0);
        let total_open = stats.filter(s => s.status == 'approved').reduce((a,b) => a + b.open_amt, 0)
        let total_cash_in = stats.filter(s => s.buy_sell_ind == 'buy').reduce((a,b) => a + (b.open_amt + b.called_amt), 0);
        let total_cash_out = stats.reduce((a,b) => a + (b.winnings + b.cash_rcvd), 0)
        let total_volume = stats.reduce((a,b) => a + b.called_amt, 0);
        let total_paid = total_cash_out
        //let total_orders = stats.reduce((a,b) => a + b.open_amt, 0);
        let total_cancelled = stats.filter(s => s.buy_sell_ind == 'buy').filter(s => s.status == 'cancelled').reduce((a,b) => a + b.open_amt, 0);
        total_cash_out += total_cancelled
        let commission = stats.reduce((a,b) => a + b.commission, 0)
        let reconciliation = total_cash_in - total_cash_out
        let routing_prem = 0
        if(closed && reconciliation > 0){
            routing_prem = reconciliation
        }
        reconciliation -= routing_prem


        return { reconciliation, total_paid, commission, total_unresolved, total_cash_in, total_cash_out, routing_prem, total_open, total_volume, closed} //- total_commission
    },
    getSupportedMarkets: (contest_type:string, markets:MarketProps[], event?:EventProps, tournament?:TournamentProps, match?:MatchProps) => {
        switch(contest_type){
            case 'team':
                if(!event?.supported_markets){ return [] }
                return markets.filter(mk => event.supported_markets?.find(sm => sm.market_id == mk.market_id));
            case 'tournament':
                if(!tournament?.supported_markets){ return [] }
                return markets.filter(mk => tournament.supported_markets?.find(sm => sm.market_id == mk.market_id)); 
            case 'match':
                if(!match?.supported_markets){ return [] }
                return markets.filter(mk => match.supported_markets?.find(sm => sm.market_id == mk.market_id));
            default: return []
        }
    },
    getVar1Label: (market:MarketProps, var_1:number, side?:string) => {
        if(side == 'over'){ return `O ${var_1}` }
        if(side == 'under'){ return `U ${var_1}` }
        if(market.type != 'Spread'){ return `${var_1}` }
        if(var_1 < 0){ return `${var_1}` }
        return `+${var_1}`
    },
    getOddsLabel: (odds:number, decimals?:number) => {
        let new_odds = odds.toFixed()
        if(decimals){ new_odds = odds.toFixed(decimals) }
        if(odds < 0){ return `${new_odds}` }
        return `+${new_odds}`
    },
    calcAmericanOddsFromProbability: (probability:number) => {
        let p = probability*100; //Convert to whole number
        if (p >= 100){ return -99999 }
        if(p === 0){ return 9999 }
        if(p === 50){ return 100 }
        if(p < 50){
            return (100/(p/100))-100
        }
        return (p/(1-(p/100)))*-1
    },
    getSideIds: (contest_type:string, so:MarketSideOptionProps, market?:MarketProps, event?:EventProps, tournament?:TournamentProps, match?:MatchProps, athlete?:AthleteProps, team?:TeamProps) => {
        if(!market){ return undefined }
        switch(contest_type){
            case 'team':
                if(!event) return undefined
                return MarketComponentHelpers.getTeamEventSideIds(event, market, so, athlete, team)
            case 'tournament':
                if(!tournament){ return undefined }
                return MarketComponentHelpers.getTournamentSideIds(market, so, athlete, team);
            case 'match':
                if(!match){ return undefined }
                return MarketComponentHelpers.getMatchSideIds(match, so)
            default: return undefined
        }
    },
    getTournamentSideIds: (market:MarketProps, side_option:MarketSideOptionProps, athlete?:AthleteProps, team?:TeamProps) => {
        const o_side = market.side_options.find(so => so.side != side_option.side);
        if(!o_side){ return undefined }
        switch(side_option.id_source){
            case 'side':
                return { side_id: side_option.side, reversed_side_id: o_side.side }
            case 'athlete':
                if(!athlete){ return undefined }
                return { side_id: athlete.athlete_id, reversed_side_id: athlete.athlete_id }
            case 'team':
                if(market.level == 'team'){
                    if(!team){ return undefined }
                    return { side_id: team.team_id, reversed_side_id: team.team_id }
                }
                return undefined
            default: return undefined
        }
    },
    pretendFulfillOrder: (order:BestAvailableOrderProps | OrderProps, stake?:number):OrderProps => {
        let fill_stake = stake ?? order.open_amt
        if(fill_stake > order.open_amt){
            fill_stake = order.open_amt
        }
        order.open_amt -= fill_stake
        order.called_amt = fill_stake

        let position:PositionProps = {
            position_id: '',
            order_id: order.order_id,
            stake: fill_stake,
            probability: order.probability,
            odds: order.odds,
            status: 'inprogress',
            winnings: 0, net_winnings: 0,
            potential_winnings: MarketComponentHelpers.calcPotentialWinnings(fill_stake, order.odds),
            original_stake: fill_stake,
            cash_rcvd: 0,
            delayed_cash: 0,
            in_sale_order: 0,
            commission: 0
        }
        return {
            ...order,
            be_type: 'market',
            order_type:'limit',
            collar_pct: 0,
            position_ids: [],
            market_type: 'FOR_MONEY',
            status: 'approved',
            available_for_parlays:false,
            resolution_status: 'inprogress',
            commission_pct: 0,
            side_type: order.side_type as 'team'|'side'|'athlete',
            buy_sell_ind: order.buy_sell_ind as 'buy'|'sell',
            event_type: order.event_type as 'team'|'tournament'|'match',
            positions: [position]
        }
    },
    prettyKey : (key?:string) => {
        try {
            if(!key){ return '' }
            let nested_key = key.split(':')
            let last_key = nested_key[nested_key.length - 1]
            if(!last_key){ return key }
            let key_split = last_key.split('_')
            let pretty_key = ''
            key_split.map((k,i) => {
              let last_item = i == k.length -1 ? true : false
              let first_letter = k.charAt(0)
              first_letter = first_letter.toUpperCase()
              pretty_key += `${first_letter}${k.substring(1)}`
              if(!last_item){ pretty_key += ' ' }
            })
            return pretty_key   
        } catch (e) {
            console.log(e)
            return key
        }
      },
      getMatchSideIds: (match:MatchProps, side_option:MarketSideOptionProps) => {
        if(side_option.side == 'home'){ 
            return { side_id: match.participants[1], reversed_side_id: match.participants[0] } 
        } else {
            return { side_id: match.participants[0], reversed_side_id: match.participants[1] }
        }
      },
    getTeamEventSideIds:(event:EventProps, market:MarketProps, side_option:MarketSideOptionProps, athlete?:AthleteProps, team?:TeamProps) => {
        const o_side = market.side_options.find(so => so.side != side_option.side);
        if(!o_side){ return undefined }
        switch(side_option.id_source){
            case 'side':
                return { side_id: side_option.side, reversed_side_id: o_side.side }
            case 'athlete':
                if(!athlete){ return undefined }
                return { side_id: athlete.athlete_id, reversed_side_id: athlete.athlete_id }
            case 'team':
                if(market.level == 'event'){
                    let side_id = event[side_option.side as keyof EventProps]?.team_id
                    let reversed_side_id = event[o_side.side as keyof EventProps]?.team_id
                    if(!side_id || !reversed_side_id){ return undefined }
                    return { side_id, reversed_side_id }
                }
                if(market.level == 'team'){
                    if(!team){ return undefined }
                    return { side_id: team.team_id, reversed_side_id: team.team_id }
                }
                return undefined
            default: return undefined
        }
    },
    isEventMarketLocked : (event:EventProps, market_id:string) => {
        if(!event.supported_markets){ return true }
        let supp_market = event.supported_markets.find(sm => sm.market_id == market_id)
        if(!supp_market){ return true }
        if(supp_market.removed){ return true }
        if(['closed','complete','postponed','suspended'].includes(event.status)){ return true }
        return false
    },
    isTournamentMarketLocked: (tournament:TournamentProps, market_id:string) => {
        if(!tournament?.supported_markets){ return true }
        let supp_market = tournament.supported_markets.find(sm => sm.market_id == market_id)
        if(!supp_market){ return true }
        if(supp_market.removed){ return true }
        if(['closed','complete'].includes(tournament.status)){ return true }
        return false
    },
    isMatchMarketLocked: (match:MatchProps, market_id:string) => {
        if(!match.supported_markets){ return true }
        let supp_market = match.supported_markets.find(sm => sm.market_id == market_id);
        if(['cancelled','postponed','closed','complete'].includes(match.status)){ return true }
        if(!supp_market){ return true }
        if(supp_market.removed){ return true }
        return false
    },
    getMatchTimeDetail: (match:MatchProps) => {
        if(!match.time_detail){
            if(match.status == 'not_started'){ return moment(match.scheduled_datetime).format('MMM DD hh:mm a') }
            return match.status.toUpperCase()
        }
        if(match.time_detail == 'scheduled'){ return moment(match.scheduled_datetime).format('MMM DD hh:mm a') }
        return match.time_detail
    },
    getOrderTitle: (order:OrderProps|BestAvailableOrderProps, market:MarketProps, event?:EventProps, tournament?:TournamentProps, match?:MatchProps, athlete?:AthleteProps, team?:TeamProps) => {
        switch(order.event_type){
            case 'team':
                if(!event){ return undefined }
                return MarketComponentHelpers.getOrderTitleForTeamEvent(order, market, event, athlete)
            case 'tournament':
                if(!tournament){return undefined }
                return MarketComponentHelpers.getOrderTitleForTournament(order, market, tournament, athlete, team);
            case 'match':
                if(!match){ return undefined }
                return MarketComponentHelpers.getOrderTitleForMatch(order, market, match, athlete, team);
            default: return undefined
        }
    },
    getOrderTitleForTeamEvent : (order:OrderProps|BestAvailableOrderProps, market:MarketProps, event:EventProps, athlete?:AthleteProps, exotic?:any):string | undefined => {
        let market_side = market.side_options.find(o => o.side == order.side)
        if(!market_side){ return undefined }
        var re = /({.*?})/;
        let parsed_text = market_side.parseable_title.split(re)
        let new_title = '';
        parsed_text.map(t => {
            if(t === ''){ return }
            if(t[0] !== '{'){ return new_title += t }
            let variable = t.slice(1)
            variable = variable.slice(0, variable.length -1)
            switch(variable){
                case 'team': 
                    let team = event[order.side as keyof EventProps]
                    return new_title += team.market_name
                case 'var_1': return new_title += MarketComponentHelpers.getVar1Label(market, order.var_1)
                case 'athlete': return new_title += athlete?.abbr_name
                case 'stat': return new_title += market.stat_label ?? market.stat
                case 'exotic': return new_title += exotic?.name 
                default: return

            }
        })
        return new_title
    },
    getOrderTitleForTournament : (order:OrderProps | BestAvailableOrderProps, market:MarketProps, tournament:TournamentProps, athlete?:AthleteProps, team?:TeamProps):string | undefined => {
        if(!tournament){ return undefined }
        let market_side = market.side_options.find(o => o.side == order.side)
        if(!market_side){ return order.side }
        var re = /({.*?})/;
        let parsed_text = market_side.parseable_title.split(re)
        let new_title = '';
        parsed_text.map(t => {
            if(t === ''){ return }
            if(t[0] !== '{'){ return new_title += t }
            let variable = t.slice(1)
            variable = variable.slice(0, variable.length -1)
            switch(variable){
                case 'team': return new_title += `${team?.market_name} ${team?.name}`
                case 'var_1': return new_title += MarketComponentHelpers.getVar1Label(market, order.var_1)
                case 'athlete': return new_title += athlete?.abbr_name
                case 'stat': return new_title += market.stat 
                default: return

            }
        })
        return new_title
    },
    getOrderTitleForMatch : (order:OrderProps|BestAvailableOrderProps, market:MarketProps, match:MatchProps, athlete?:AthleteProps, team?:TeamProps):string | undefined => {
        if(!match){ return undefined }
        let market_side = market.side_options.find(o => o.side == order.side)
        if(!market_side){ return order.side }
        var re = /({.*?})/;
        let parsed_text = market_side.parseable_title.split(re)
        let new_title = '';
        parsed_text.map(t => {
            if(t === ''){ return }
            if(t[0] !== '{'){ return new_title += t }
            let variable = t.slice(1)
            variable = variable.slice(0, variable.length -1)
            switch(variable){
                case 'team': return new_title += `${team?.market_name} ${team?.name}`
                case 'var_1': return new_title += MarketComponentHelpers.getVar1Label(market, order.var_1)
                case 'athlete': return new_title += athlete?.abbr_name
                case 'stat': return new_title += market.stat 
                default: return

            }
        })
        return new_title
    },
    getExternalPriceDetails: (contest_type:string, market?:MarketProps, event?:EventProps, tournament?:TournamentProps, match?:MatchProps) => {
        let fail_response = { contest_title: '', o_side_label:'', trade_side_label: '' }
        if(!market){ return fail_response }
        fail_response.o_side_label = market.side_options.find(so => so.side != market.trade_side)?.side.toUpperCase() ?? '',
        fail_response.trade_side_label = market.trade_side.toUpperCase()

        let contest_title = ''
        let o_side_label = fail_response.o_side_label
        let trade_side_label = fail_response.trade_side_label

        market.side_options.map(so => {
            if(so.id_source == 'side'){
                if(so.side == market.trade_side){ trade_side_label = so.side.toUpperCase() }
                else { o_side_label = so.side.toUpperCase() }
                return
            }
            switch(contest_type){
                case 'team':
                    if(!event){ break }
                    contest_title = event.event_title
                    if(so.id_source == 'team'){
                        if(so.side == market.trade_side){ trade_side_label = event[so.side as keyof EventProps]?.abbr }
                        else { o_side_label = event[so.side as keyof EventProps]?.abbr }
                        break
                    }
                    break  
                case 'tournament':
                    if(!tournament){ break }
                    contest_title = tournament.tournament_name
                    break
                case 'match':
                    if(!match){ return }
                    contest_title = match.match_title
                    break
                default:
            }
        })
        return { contest_title, o_side_label, trade_side_label }
    },
    setMarket: (unit_size:number, contest_type:'team'|'tournament'|'match',best_available:BestAvailableOrderProps[], external_prices:ExternalPriceProps[], o_side_margin:number, trade_side_margin:number, market?:MarketProps, event?:EventProps, tournament?:TournamentProps, match?:MatchProps, athlete?:AthleteProps, team?:TeamProps) => {
            let orders:OrderProps[] = []
            let expire_datetime:Moment | undefined = undefined
            if(!market){ return [] }
            market.side_options.map(so => {
                let side_id_response:any = undefined
                switch(contest_type){
                    case 'team':
                        if(!event){ return }
                        expire_datetime = moment(event.scheduled_datetime);
                        side_id_response = MarketComponentHelpers.getTeamEventSideIds(event, market, so);
                        break
                    case 'tournament':
                        if(!tournament){ return }
                        expire_datetime = moment(tournament.scheduled_datetime);
                        side_id_response = MarketComponentHelpers.getTournamentSideIds(market, so, athlete, team);
                        break
                    case 'match':
                        if(!match){ return }
                        expire_datetime = moment(match.scheduled_datetime);
                        side_id_response = MarketComponentHelpers.getMatchSideIds(match, so);
                        break
                    default: return
                }
                 
                if(!side_id_response){ return }
                let ba = best_available.find(ba => ba.side == so.side)
                let o_ba = best_available.find(ba => ba.side != so.side);
                if(ba && o_ba){
                    let ba_trade = MarketMaketHelpers.getTradeFromAvailableOrder(ba, side_id_response.reversed_side_id);
                    let ba_order = MarketMaketHelpers.getOrderFromTrade(ba_trade, 10, ba.order_id);
                    let ba_title = MarketComponentHelpers.getOrderTitle(ba_order, market, event, tournament, match, athlete, team);
    
                    if(!ba_title){ return }
                    return orders.push({ ...ba_order, title: ba_title })
                }
                let ep = external_prices.find(p => p.side == so.side);
                let o_ep = external_prices.find(p => p.side != so.side);
                if(ep && o_ep){
                    let ep_trade = MarketMaketHelpers.getTradeFromPrice(ep, side_id_response.reversed_side_id);
                    let ep_order = MarketMaketHelpers.getOrderFromTrade(ep_trade, 10);
                    let ep_title = MarketComponentHelpers.getOrderTitle(ep_order, market, event, tournament, match, athlete, team);
                    if(!ep_title){ return }
                    return orders.push({ ...ep_order, title: ep_title })
                }
                return
            })
    
            if(orders.length != 2){ return [] }
    
            //OK!! Lets figured out the buffer odds!
            let no_vig_trade_price = .5
            let no_vig_o_price = .5
            let no_vig_data = MarketMaketHelpers.getNoVigPrice(external_prices, market);
            if(no_vig_data){
                no_vig_o_price = no_vig_data.o_side_prob
                no_vig_trade_price = no_vig_data.trade_side_prob
            }
    
            //Now lets add a buffer to each one based on the margin provided!
            let margin = o_side_margin + trade_side_margin
            let o_side_weight = o_side_margin / margin
            let trade_side_weight = trade_side_margin / margin
            no_vig_trade_price -= (margin * trade_side_weight)
            let novig_trade_side_odds = Math.round(MarketMaketHelpers.calcAmericanOddsFromProbability(no_vig_trade_price))
    
            no_vig_o_price -= (margin * o_side_weight)
    
            let novig_o_side_odds = Math.round(MarketMaketHelpers.calcAmericanOddsFromProbability(no_vig_o_price))
    
            //OK!!! Now lets add the propbabilities to the orders!
            let trade_side_order = orders.find(o => o.side == market.trade_side);
            let o_side_order = orders.find(o => o.side != market.trade_side);
            if(!trade_side_order || !o_side_order){ return [] }
    
            let higher_probability_order = no_vig_trade_price >= no_vig_o_price ? 'trade' : 'other'
            let trade_side_stake = unit_size, o_side_stake = unit_size
            if(higher_probability_order == 'trade'){
                //If that is the stake, then the potential winnigns is
                let pot_win = MarketMaketHelpers.calcPotentialWinnings(trade_side_stake, novig_trade_side_odds);
                o_side_stake = MarketMaketHelpers.calcSaleValue(novig_o_side_odds, pot_win);
            } else {
                //If that is the stake, then the potential winnigns is
                let pot_win = MarketMaketHelpers.calcPotentialWinnings(o_side_stake, novig_o_side_odds);
                trade_side_stake = MarketMaketHelpers.calcSaleValue(novig_trade_side_odds, pot_win);
            }
    
            o_side_order = {
                ...o_side_order,
                probability: no_vig_o_price,
                odds: novig_o_side_odds,
                fulfill_action: 'drain',
                expire_datetime: expire_datetime,
                potential_winnings: MarketMaketHelpers.calcPotentialWinnings(o_side_stake, novig_o_side_odds),
                open_amt: o_side_stake,
            }
            trade_side_order = {
                ...trade_side_order,
                probability: no_vig_trade_price,
                odds: novig_trade_side_odds,
                fulfill_action: 'drain',
                expire_datetime: expire_datetime,
                potential_winnings: MarketMaketHelpers.calcPotentialWinnings(trade_side_stake, novig_trade_side_odds),
                open_amt: trade_side_stake,
            }
    
            return [ o_side_order, trade_side_order ]
    },
    getUniqueDatesFromEvents: (events:EventProps[]):string[] => {
        const unique_dates = [ ...new Set(events.map(e => moment(e.scheduled_datetime).format('YYYY-MM-DD'))) ].sort((a,b) => moment(a).unix() - moment(b).unix())
        return unique_dates.sort((a,b) => moment(a).unix() - moment(b).unix())
    },
    getClosestDay: (schedule_range:string[]):string => {
        let closest:any|undefined;
        let exact_day = schedule_range.find(d => moment(d).format('YYYY-MM-DD') == moment().format('YYYY-MM-DD'))
        if(exact_day){ return moment(exact_day).format('YYYY-MM-DD') }
        schedule_range.map(d => {
            if(!closest){ closest = moment(d) }
            if(moment(d).isBefore(moment())){ return } //Day is in the past
            let new_range = moment(d).diff(moment(), 'days')
            let closest_range = closest.diff(moment(), 'days')
            if(Math.abs(new_range) < Math.abs(closest_range)){ closest = moment(d) }
        })
        return closest.format('YYYY-MM-DD')
    },
    getUniqueTimesFromEvents: (events:EventProps[]) => {
        const unique_times = [ ...new Set(events.map(e => moment(e.scheduled_datetime).format('h:mm a'))) ]
        return unique_times.sort((a,b) => moment(a, 'h:mm a').unix() - moment(b, 'h:mm a').unix())
    },
    getUniqueDivisions: (events:EventProps[]):string[] => {
        let away_divisions = events.map(e => e.away?.division ?? '')
        let home_divisions = events.map(e => e.home?.division ?? '')
        let divisions = away_divisions.concat(home_divisions)
        let options = [ ...new Set(divisions.map(d => d)) ].sort((a,b) => a > b ? 1 : -1)
        let ranks = events.find(e => e.away?.rank || e.home?.rank)
        if(ranks){ options = ['Top25'].concat(options) }
        return options
    },
    getUniqueSubDivisions: (events:EventProps[]):string[] => {    
        let away_divisions = events.map(e => e.away?.sub_division ?? '')
        let home_divisions = events.map(e => e.home?.sub_division ?? '')
        return [ ...new Set(away_divisions.concat(home_divisions).map(d => d)) ].sort((a,b) => a > b ? 1 : -1)
    },
    getFinalizedStatuses:() => {
        return [
            'complete',
            'closed',
            'suspended',
            'cancelled',
            'canceled',
            'not_necessary'
        ]
    },
    FINALIZED_STATUSES: [
        'closed',
        'complete',
        'cancelled',
        'unnecessary',
        'canceled',
        'postponed',
        'suspended'
    ],
    IN_PROGRESS_STATUSES: [
        'inprogress',
        'halftime',
        'intermission'
    ]
}